"use client";

import React, { useEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import { urlFor } from "@/sanity/lib/image";
import { SanityDocument } from "@sanity/client";
import Image from "next/image";
import { AspectRatio } from "@components/ui/aspect-ratio";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import SectionHeader from "@components/common/section-header";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Element } from "react-scroll";

interface IntroductionProps {
  services: SanityDocument[];
  homeIntro: SanityDocument;
}

function Introduction({ services, homeIntro }: Readonly<IntroductionProps>) {
  const displayServices = services
    .toSorted(
      (a, b) =>
        new Date(b._updatedAt).getTime() - new Date(a._updatedAt).getTime(),
    )
    .slice(0, 5);
  const introImgSRC = urlFor(homeIntro.image).format("webp").url();
  const [introAspect, setIntroAspect] = useState<number>(1 / 1);
  const isDesktop = useMediaQuery("( min-width: 1024px)");
  const introRef = useRef(null);
  const servicesRef = useRef<HTMLDivElement[]>([]);
  servicesRef.current = [];

  useEffect(() => {
    if (isDesktop) {
      setIntroAspect(1 / 1);
    } else {
      setIntroAspect(4 / 3);
    }
  }, [isDesktop]);

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);
      servicesRef.current.forEach((service) => {
        gsap.fromTo(
          service,
          { autoAlpha: 0, x: -50 },
          {
            autoAlpha: 1,
            x: 0,
            duration: 0.5,
            scrollTrigger: {
              trigger: service,
              start: "top bottom-=150",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    },
    { scope: introRef },
  );

  const addtoRefs = (element: HTMLDivElement | null): void => {
    if (element && !servicesRef.current.includes(element)) {
      servicesRef.current.push(element);
    }
  };

  return (
    <Element name="introduction">
      <section
        ref={introRef}
        className="relative min-h-screen w-full py-6 lg:pb-20"
      >
        <article className="mx-auto flex h-full w-full max-w-7xl flex-col gap-6">
          <SectionHeader
            highlightedTitle="Building Brilliance:"
            title="What We Offer at Rake Engineering Consultancy"
          />
          <div className="flex h-full w-full flex-col items-center justify-center gap-10 p-5 lg:flex-row lg:gap-0 lg:p-0">
            <div className="h-full w-full lg:m-10">
              <AspectRatio
                ratio={introAspect}
                className="overflow-clip object-cover shadow-md drop-shadow-lg"
              >
                <Image
                  src={introImgSRC}
                  alt={homeIntro.image.alt}
                  width={580}
                  height={500}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </AspectRatio>
            </div>
            <div className="my-auto flex h-full w-full flex-col items-center justify-around gap-9 lg:mx-10 lg:gap-5 xl:gap-9">
              {displayServices.map(({ title, description }) => (
                <div
                  key={title}
                  ref={addtoRefs}
                  className="services flex flex-col gap-3 opacity-0"
                >
                  <h3 className="flex items-center gap-3 font-Satoshi-Bold text-xl text-slate-900 lg:text-lg xl:text-xl">
                    <CheckCircleIcon className="size-8 fill-green-600 md:size-8" />
                    <span>{title}</span>
                  </h3>
                  <p className="font-Satoshi-Medium text-base text-slate-900 lg:text-sm xl:text-base">
                    {description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </article>
      </section>
    </Element>
  );
}

export default Introduction;
